import React, { useState, useEffect } from 'react';
import { Search, Users, Mail, Phone, Eye, Loader, Plus, Edit, X, Shield, Crown } from 'lucide-react';
import { peopleService } from './peopleService';
import { userService } from '../../lib/userService';
import { AddPersonModal } from './AddPersonModal';
import { EditPersonModal } from './EditPersonModal';
import { Person, AppUser } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { FirebaseImage } from '../../components/shared/FirebaseImage';

interface SearchSuggestion {
  type: 'person' | 'skill';
  text: string;
  count?: number;
  person?: Person;
}

export const PeopleModule: React.FC = () => {
  const { user, permissions } = useAuth();
  const [people, setPeople] = useState<Person[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<SearchSuggestion[]>([]);
  const [showAllSkills, setShowAllSkills] = useState(false);

  useEffect(() => {
    loadPeople();
    loadUsers(); // Load user data for admin badges
  }, []);

  useEffect(() => {
    if (searchTerm.length > 0) {
      generateSearchSuggestions();
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  }, [searchTerm, people]);

  const loadPeople = async () => {
    try {
      setLoading(true);
      const fetchedPeople = await peopleService.getPeople();
      setPeople(fetchedPeople);
    } catch (error) {
      console.error('Failed to load people:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      // Only load user data if we have permission to view users
      if (permissions.canViewUsers) {
        const fetchedUsers = await userService.getAllUsers();
        setUsers(fetchedUsers);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      // Don't show error to user since this is just for badges
    }
  };

  // Get user role for a person by email
  const getUserRole = (email: string): 'owner' | 'admin' | 'user' | 'view-only' | null => {
    const appUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());
    return appUser?.role || null;
  };

  // Check if person has admin privileges (owner or admin)
  const isAdmin = (email: string): boolean => {
    const role = getUserRole(email);
    return role === 'owner' || role === 'admin';
  };

  // Check if current user can edit a person's profile
  const canEditPerson = (person: Person): boolean => {
    if (!user) return false;
    
    // Use the permissions system
    const isOwnProfile = user.email === person.email;
    return permissions.canEditAnyProfile || isOwnProfile;
  };

  const handleEditPerson = (person: Person) => {
    setEditingPerson(person);
    setShowEditModal(true);
  };

  // Handle successful edit - refresh the selected person data
  const handleEditSuccess = async () => {
    await loadPeople(); // Refresh all people data
    
    // If we have a selected person, update it with the latest data
    if (selectedPerson) {
      const updatedPeople = await peopleService.getPeople();
      const updatedSelectedPerson = updatedPeople.find(p => p.id === selectedPerson.id);
      if (updatedSelectedPerson) {
        setSelectedPerson(updatedSelectedPerson);
      }
    }
  };

  // Generate initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get all skills from all people with counts (unique people per skill)
  const getAllSkillsWithCounts = () => {
    const skillCounts = new Map<string, number>();
    
    people.forEach(person => {
      // Get all skills for this person and remove duplicates
      const allPersonSkills = [
        ...person.verktoyOgSystemer,
        ...person.spesialiserteVerktoyUtvikler,
        ...person.spesialiserteVerktoyDesigner,
        ...person.spesialiserteVerktoyMarkedsforing,
        ...person.spesialiserteVerktoyProsjektledelse,
        ...person.spesialiserteVerktoy3D,
        ...person.spesialiserteVerktoyAI,
      ];
      
      // Use Set to remove duplicates for this person
      const uniquePersonSkills = new Set(allPersonSkills.filter(skill => skill.trim()));
      
      // Count each unique skill once per person
      uniquePersonSkills.forEach(skill => {
        skillCounts.set(skill, (skillCounts.get(skill) || 0) + 1);
      });
    });
    
    return Array.from(skillCounts.entries())
      .map(([skill, count]) => ({ skill, count }))
      .sort((a, b) => b.count - a.count);
  };

  // Get popular skills (top 10)
  const getPopularSkills = () => {
    return getAllSkillsWithCounts().slice(0, 10);
  };

  // Generate search suggestions
  const generateSearchSuggestions = () => {
    const suggestions: SearchSuggestion[] = [];
    const searchLower = searchTerm.toLowerCase();
    
    // Add people suggestions
    people.forEach(person => {
      const personText = `${person.name} ${person.title} ${person.department} ${person.email}`.toLowerCase();
      if (personText.includes(searchLower)) {
        suggestions.push({
          type: 'person',
          text: `${person.name} (${person.title})`,
          person: person
        });
      }
    });
    
    // Add skill suggestions
    const skillCounts = getAllSkillsWithCounts();
    skillCounts.forEach(({ skill, count }) => {
      if (skill.toLowerCase().includes(searchLower)) {
        suggestions.push({
          type: 'skill',
          text: skill,
          count: count
        });
      }
    });
    
    // Limit suggestions and prioritize exact matches
    const exactPersonMatches = suggestions.filter(s => s.type === 'person' && s.text.toLowerCase().startsWith(searchLower));
    const exactSkillMatches = suggestions.filter(s => s.type === 'skill' && s.text.toLowerCase().startsWith(searchLower));
    const otherMatches = suggestions.filter(s => 
      !(s.type === 'person' && s.text.toLowerCase().startsWith(searchLower)) &&
      !(s.type === 'skill' && s.text.toLowerCase().startsWith(searchLower))
    );
    
    const finalSuggestions = [
      ...exactPersonMatches.slice(0, 3),
      ...exactSkillMatches.slice(0, 5),
      ...otherMatches.slice(0, 4)
    ].slice(0, 8);
    
    setSearchSuggestions(finalSuggestions);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    // Add as active filter if not already present (both people and skills)
    if (!activeFilters.includes(suggestion.text)) {
      setActiveFilters(prev => [...prev, suggestion.text]);
    }
    setSearchTerm('');
    setShowSuggestions(false);
  };

  // Handle popular skill click
  const handlePopularSkillClick = (skill: string) => {
    if (!activeFilters.includes(skill)) {
      setActiveFilters(prev => [...prev, skill]);
    }
  };

  // Remove active filter
  const removeFilter = (filterToRemove: string) => {
    setActiveFilters(prev => prev.filter(filter => filter !== filterToRemove));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setActiveFilters([]);
    setSearchTerm('');
  };

  // Filter people based on search term and active filters
  const filteredPeople = people.filter(person => {
    // Apply active filters with AND logic
    const passesActiveFilters = activeFilters.every(filter => {
      const filterLower = filter.toLowerCase();
      
      // Special handling for person name filters (format: "Name (Title)")
      const personNameMatch = filter.match(/^(.+?)\s*\((.+?)\)$/);
      if (personNameMatch) {
        const [, name, title] = personNameMatch;
        return person.name.toLowerCase().includes(name.toLowerCase()) && 
               person.title.toLowerCase().includes(title.toLowerCase());
      }
      
      // Check basic info
      const basicMatch = [
        person.name,
        person.title,
        person.department,
        person.email,
        person.bio,
        person.arbeidsoppgaver,
        person.andreFerdigheter
      ].some(field => field.toLowerCase().includes(filterLower));
      
      // Check all skills
      const allSkills = [
        ...person.verktoyOgSystemer,
        ...person.spesialiserteVerktoyUtvikler,
        ...person.spesialiserteVerktoyDesigner,
        ...person.spesialiserteVerktoyMarkedsforing,
        ...person.spesialiserteVerktoyProsjektledelse,
        ...person.spesialiserteVerktoy3D,
        ...person.spesialiserteVerktoyAI,
      ];
      
      const skillMatch = allSkills.some(skill => skill.toLowerCase().includes(filterLower));
      
      return basicMatch || skillMatch;
    });
    
    if (!passesActiveFilters) return false;
    
    // Apply current search term if present
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search in basic info
    const basicMatch = [
      person.name,
      person.title,
      person.department,
      person.email,
      person.bio,
      person.arbeidsoppgaver,
      person.andreFerdigheter
    ].some(field => field.toLowerCase().includes(searchLower));
    
    // Search in all skills
    const allSkills = [
      ...person.verktoyOgSystemer,
      ...person.spesialiserteVerktoyUtvikler,
      ...person.spesialiserteVerktoyDesigner,
      ...person.spesialiserteVerktoyMarkedsforing,
      ...person.spesialiserteVerktoyProsjektledelse,
      ...person.spesialiserteVerktoy3D,
      ...person.spesialiserteVerktoyAI,
    ];
    
    const skillMatch = allSkills.some(skill => skill.toLowerCase().includes(searchLower));
    
    return basicMatch || skillMatch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin" size={32} />
        <span className="ml-2">Loading people...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Users size={32} className="text-primary" />
          <div>
            <h2 className="text-2xl font-bold">Team Directory</h2>
            <p className="text-muted-foreground">{people.length} team members</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button 
            onClick={() => setShowAddModal(true)}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={20} />
            Add Person
          </button>
        </div>
      </div>

      {/* Search Section */}
      <div className="space-y-4">
        {/* Main Search Input */}
        <div className="relative max-w-2xl">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
          <input
            type="text"
            placeholder="Search people and skills..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => searchTerm.length > 0 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="pl-10 pr-10 py-3 border rounded-lg w-full text-base"
          />
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm('');
                setShowSuggestions(false);
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={20} />
            </button>
          )}
          
          {/* Search Suggestions Dropdown */}
          {showSuggestions && searchSuggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 z-10 max-h-64 overflow-y-auto">
              {searchSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    {suggestion.type === 'person' ? (
                      <Users size={16} className="text-blue-500" />
                    ) : (
                      <Search size={16} className="text-green-500" />
                    )}
                    <span className="font-medium">{suggestion.text}</span>
                  </div>
                  {suggestion.type === 'skill' && suggestion.count && (
                    <span className="text-xs text-muted-foreground bg-gray-100 px-2 py-1 rounded-full">
                      {suggestion.count} {suggestion.count === 1 ? 'person' : 'people'}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground font-medium">Active filters:</span>
              <div className="flex flex-wrap gap-2">
                {activeFilters.map((filter) => (
                  <span
                    key={filter}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full border border-blue-200"
                  >
                    {filter}
                    <button
                      onClick={() => removeFilter(filter)}
                      className="hover:text-blue-600"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  Clear all
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Popular Skills */}
        {!searchTerm && activeFilters.length === 0 && people.length > 0 && (
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Popular skills:</p>
            <div className="flex flex-wrap gap-2">
              {(showAllSkills ? getAllSkillsWithCounts() : getPopularSkills()).map(({ skill, count }) => (
                <button
                  key={skill}
                  onClick={() => handlePopularSkillClick(skill)}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors flex items-center gap-1"
                >
                  {skill}
                  <span className="text-xs text-blue-500">({count})</span>
                </button>
              ))}
              {!showAllSkills && getAllSkillsWithCounts().length > 10 && (
                <button
                  onClick={() => setShowAllSkills(true)}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors"
                >
                  See all ({getAllSkillsWithCounts().length})
                </button>
              )}
              {showAllSkills && (
                <button
                  onClick={() => setShowAllSkills(false)}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors"
                >
                  Show less
                </button>
              )}
            </div>
          </div>
        )}

        {/* Search Results Summary */}
        {(searchTerm || activeFilters.length > 0) && (
          <div className="text-sm text-muted-foreground">
            Found {filteredPeople.length} {filteredPeople.length === 1 ? 'person' : 'people'}
            {activeFilters.length > 0 && ` matching ${activeFilters.length} filter${activeFilters.length === 1 ? '' : 's'}`}
            {searchTerm && ` and search term "${searchTerm}"`}
          </div>
        )}
      </div>

      {/* Empty state */}
      {filteredPeople.length === 0 && people.length > 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No people found matching your filters.</p>
          {(activeFilters.length > 0 || searchTerm) && (
            <button
              onClick={clearAllFilters}
              className="text-primary hover:underline mt-2"
            >
              Clear all filters to see all people
            </button>
          )}
        </div>
      )}

      {filteredPeople.length === 0 && people.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            No team members added yet. Click 'Add Person' to get started!
          </p>
        </div>
      )}

      {/* People Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredPeople.map((person) => (
          <div key={person.id} className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow flex flex-col relative">
            {/* Admin Badge - Top Right Corner */}
            {isAdmin(person.email) && (
              <div className="absolute top-3 right-3">
                <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 border border-blue-200">
                  <Crown size={12} />
                  Admin
                </div>
              </div>
            )}

            {/* Profile Picture / Avatar */}
            <div className="flex justify-center mb-4">
              <FirebaseImage
                src={person.profilePictureUrl}
                storagePath={person.profilePicturePath}
                alt={person.name}
                className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                fallback={
                  <div className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-semibold border-2 border-gray-200">
                    {getInitials(person.name)}
                  </div>
                }
              />
            </div>

            {/* Basic Info - All centered */}
            <div className="text-center space-y-3 flex-1 flex flex-col">
              <div>
                <h3 className="text-lg font-semibold">{person.name}</h3>
                <p className="text-sm text-foreground font-medium">{person.title}</p>
                <p className="text-xs text-muted-foreground">{person.department}</p>
              </div>

              {/* Contact Info - Centered */}
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Mail size={14} />
                  <a 
                    href={`mailto:${person.email}`}
                    className="truncate hover:text-primary transition-colors hover:underline"
                  >
                    {person.email}
                  </a>
                </div>
                {person.phone && (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Phone size={14} />
                    <a 
                      href={`tel:${person.phone}`}
                      className="hover:text-primary transition-colors hover:underline"
                    >
                      {person.phone}
                    </a>
                  </div>
                )}
              </div>

              {/* Quick Skills Preview - Centered */}
              <div className="flex flex-wrap gap-1 justify-center">
                {[
                  ...person.spesialiserteVerktoyDesigner.slice(0, 2),
                  ...person.spesialiserteVerktoyUtvikler.slice(0, 2),
                  ...person.spesialiserteVerktoyMarkedsforing.slice(0, 2),
                ].slice(0, 3).map((skill) => (
                  <span 
                    key={skill} 
                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                  >
                    {skill}
                  </span>
                ))}
                {[
                  ...person.spesialiserteVerktoyDesigner,
                  ...person.spesialiserteVerktoyUtvikler,
                  ...person.spesialiserteVerktoyMarkedsforing,
                  ...person.spesialiserteVerktoyProsjektledelse,
                  ...person.spesialiserteVerktoy3D,
                  ...person.spesialiserteVerktoyAI,
                ].length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                    +{[
                      ...person.spesialiserteVerktoyDesigner,
                      ...person.spesialiserteVerktoyUtvikler,
                      ...person.spesialiserteVerktoyMarkedsforing,
                      ...person.spesialiserteVerktoyProsjektledelse,
                      ...person.spesialiserteVerktoy3D,
                      ...person.spesialiserteVerktoyAI,
                    ].length - 3} more
                  </span>
                )}
              </div>

              {/* Spacer to push button to bottom */}
              <div className="flex-1"></div>

              {/* View Profile Button - Always at bottom */}
              <div className="mt-auto pt-2">
                <button
                  onClick={() => setSelectedPerson(person)}
                  className="w-full text-foreground py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-primary/10 transition-colors border border-gray-200 hover:border-primary/40"
                >
                  <Eye size={16} />
                  View Profile
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Profile Modal */}
      {selectedPerson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-background dark:bg-gray-800 rounded-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-8">
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-3">
                  <h2 className="text-3xl font-bold text-gray-800 dark:text-white">{selectedPerson.name}</h2>
                  {/* Admin Badge in Modal Header */}
                  {isAdmin(selectedPerson.email) && (
                    <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 border border-blue-200">
                      <Crown size={14} />
                      Admin
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {canEditPerson(selectedPerson) && (
                    <button
                      onClick={() => handleEditPerson(selectedPerson)}
                      className="bg-primary text-primary-foreground px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-primary/90 transition-colors font-medium"
                    >
                      <Edit size={18} />
                      Edit Profile
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedPerson(null)}
                    className="text-gray-400 hover:text-gray-600 text-3xl font-light"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Profile Picture & Basic Info */}
                <div className="lg:col-span-1">
                  <div className="rounded-xl p-6 text-center h-fit border border-gray-200 dark:border-gray-600">
                    <FirebaseImage
                      src={selectedPerson.profilePictureUrl}
                      storagePath={selectedPerson.profilePicturePath}
                      alt={selectedPerson.name}
                      className="w-32 h-32 rounded-full object-cover mx-auto mb-6 border-4 border-white shadow-lg"
                      fallback={
                        <div className="w-32 h-32 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-4xl font-semibold mx-auto mb-6 border-4 border-white shadow-lg">
                          {getInitials(selectedPerson.name)}
                        </div>
                      }
                    />
                    
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">{selectedPerson.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-6">{selectedPerson.department}</p>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-center gap-3 text-sm">
                        <Mail size={16} className="text-gray-400" />
                        <a 
                          href={`mailto:${selectedPerson.email}`}
                          className="text-gray-700 dark:text-gray-200 hover:text-primary transition-colors hover:underline"
                        >
                          {selectedPerson.email}
                        </a>
                      </div>
                      {selectedPerson.phone && (
                        <div className="flex items-center justify-center gap-3 text-sm">
                          <Phone size={16} className="text-gray-400" />
                          <a 
                            href={`tel:${selectedPerson.phone}`}
                            className="text-gray-700 dark:text-gray-200 hover:text-primary transition-colors hover:underline"
                          >
                            {selectedPerson.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bio and Details */}
                <div className="lg:col-span-2 space-y-6">
                  {selectedPerson.bio && (
                    <div className="rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                      <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">About</h4>
                      <p className="text-gray-700 dark:text-gray-200 leading-relaxed">{selectedPerson.bio}</p>
                    </div>
                  )}

                  {selectedPerson.arbeidsoppgaver && (
                    <div className="rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                      <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">Work Tasks</h4>
                      <p className="text-gray-700 dark:text-gray-200 leading-relaxed">{selectedPerson.arbeidsoppgaver}</p>
                    </div>
                  )}

                  {/* Skills Sections */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries({
                      'Tools & Systems': selectedPerson.verktoyOgSystemer,
                      'Developer Tools': selectedPerson.spesialiserteVerktoyUtvikler,
                      'Design Tools': selectedPerson.spesialiserteVerktoyDesigner,
                      'Marketing Tools': selectedPerson.spesialiserteVerktoyMarkedsforing,
                      'Project Management': selectedPerson.spesialiserteVerktoyProsjektledelse,
                      '3D & Animation': selectedPerson.spesialiserteVerktoy3D,
                      'AI Tools': selectedPerson.spesialiserteVerktoyAI,
                    }).map(([category, skills]) => (
                      skills.length > 0 && (
                        <div key={category} className="rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                          <h4 className="font-semibold mb-3 text-gray-800 dark:text-white text-sm uppercase tracking-wide">{category}</h4>
                          <div className="flex flex-wrap gap-2">
                            {skills.map(skill => (
                              <span 
                                key={skill}
                                className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm font-medium"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )
                    ))}
                  </div>

                  {selectedPerson.andreFerdigheter && (
                    <div className="rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                      <h4 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">Other Skills & Expertise</h4>
                      <p className="text-gray-700 dark:text-gray-200 leading-relaxed">{selectedPerson.andreFerdigheter}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Person Modal */}
      <AddPersonModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={loadPeople}
      />

      {/* Edit Person Modal */}
      <EditPersonModal
        isOpen={showEditModal}
        person={editingPerson}
        onClose={() => {
          setShowEditModal(false);
          setEditingPerson(null);
        }}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};